import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { PushNotifications } from '@capacitor/push-notifications';
import axios from 'axios';
import { API_URL, isNativeApp } from '../config';
import { translations } from '../i18n/translations';
import { openFromNotification } from '../notifyFocus';

let started = false;
let lastPushToken = null;
let channelsReady = false;
let clickBound = false;

function notifId(seed) {
  const text = String(seed || Date.now());
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 2147483647 || 1;
}

async function ensureChannels() {
  if (!isNativeApp() || channelsReady) return;
  try {
    await LocalNotifications.createChannel({
      id: 'ashiiane-calls',
      name: 'تماس‌ها',
      description: 'تماس ورودی خانواده',
      importance: 5,
      visibility: 1,
      vibration: true,
      lights: true
    });
    await LocalNotifications.createChannel({
      id: 'ashiiane-messages',
      name: 'پیام‌ها',
      description: 'پیام‌های خانواده',
      importance: 4,
      visibility: 1,
      vibration: true,
      lights: true
    });
    channelsReady = true;
  } catch (error) {
    console.error('Notification channels failed', error);
  }
}

function bindClick() {
  if (clickBound || !isNativeApp()) return;
  clickBound = true;
  try {
    const pending = LocalNotifications.addListener('localNotificationActionPerformed', (event) => {
      openFromNotification(event?.notification?.extra || {});
    });
    if (pending && typeof pending.catch === 'function') {
      pending.catch(() => {});
    }
  } catch {
    clickBound = false;
  }
}

export async function showLocalNotification({ title, body, extra, channelId, idSeed }) {
  if (!isNativeApp()) return;
  try {
    await ensureChannels();
    const id = notifId(idSeed || `${title}-${Date.now()}`);
    await LocalNotifications.schedule({
      notifications: [
        {
          id,
          title: title || translations.appName,
          body: body || '',
          extra: extra || {},
          channelId: channelId || 'ashiiane-messages',
          autoCancel: true
        }
      ]
    });
  } catch (error) {
    console.error('Local notification failed', error);
  }
}

export async function cancelCallNotification(callId) {
  if (!isNativeApp() || !callId) return;
  try {
    await LocalNotifications.cancel({
      notifications: [{ id: notifId(`call-${callId}`) }]
    });
  } catch {
    // ignore
  }
}

export async function startNativeNotifications(token) {
  if (!isNativeApp() || !token) return;

  bindClick();

  try {
    await LocalNotifications.requestPermissions();
    await ensureChannels();
  } catch (error) {
    console.error('Local notification permission failed', error);
  }

  if (started) return;
  started = true;

  try {
    let status = await PushNotifications.checkPermissions();
    if (status.receive === 'prompt') {
      status = await PushNotifications.requestPermissions();
    }
    if (status.receive !== 'granted') return;

    await PushNotifications.register();

    PushNotifications.addListener('registration', async (event) => {
      lastPushToken = event.value;
      try {
        await axios.post(
          `${API_URL}/devices`,
          { token: event.value, platform: Capacitor.getPlatform() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Failed to register device token', error);
      }
    });

    PushNotifications.addListener('registrationError', (error) => {
      console.error('Push registration error', error);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      showLocalNotification({
        title: notification.title || translations.appName,
        body: notification.body || '',
        extra: notification.data,
        channelId:
          notification.data?.type === 'incoming-call' ? 'ashiiane-calls' : 'ashiiane-messages'
      });
    });
  } catch (error) {
    console.error('Push notifications unavailable', error);
  }
}

export async function stopNativeNotifications(authToken) {
  started = false;
  if (!isNativeApp()) return;
  try {
    await PushNotifications.removeAllListeners();
    if (authToken && lastPushToken) {
      await axios.delete(`${API_URL}/devices`, {
        headers: { Authorization: `Bearer ${authToken}` },
        data: { token: lastPushToken }
      });
    }
  } catch (error) {
    // ignore
  }
  lastPushToken = null;
}
